import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, UserCircle2 } from "lucide-react";
import { setAuthToken } from "@/lib/api";

type LoginResponse = {
  token: string;
  user: string;
  roles: string[];
  expiresInSeconds: number;
};

/**
 * Dev-only sign-in. Posts to /api/v1/dev/login (provided by
 * platform-security under the local/standalone profile) and stores the
 * resulting HS256 JWT for the axios interceptor in lib/api.ts to
 * attach as a Bearer header. Production deployments expose neither
 * the dev endpoint nor this page — the operator's IdP handles auth.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState("dev.admin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch("/api/v1/dev/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user }),
      });
      if (!resp.ok) throw new Error(`Login failed (HTTP ${resp.status})`);
      const data = (await resp.json()) as LoginResponse;
      setAuthToken(data.token);
      navigate("/governance", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-57px)] max-w-md items-center justify-center px-6">
      <form
        onSubmit={onSubmit}
        className="w-full space-y-5 rounded-xl border border-atlas-slate/25 bg-white p-7 shadow-lg"
      >
        <div>
          <h1 className="font-display text-xl font-semibold text-atlas-ink">
            Sign in
          </h1>
          <p className="mt-1 text-xs text-atlas-slate">
            DatumCraft Atlas — dev token mint (local / standalone profile only).
          </p>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-atlas-ink">User</span>
          <div className="flex items-center gap-2 rounded-md border border-atlas-slate/40 px-3 py-2 focus-within:border-atlas-primary focus-within:ring-1 focus-within:ring-atlas-primary/30">
            <UserCircle2 size={14} className="text-atlas-slate" />
            <input
              value={user}
              onChange={(e) => setUser(e.target.value)}
              className="w-full bg-transparent text-sm outline-none"
              autoFocus
              autoComplete="username"
            />
          </div>
        </label>

        {error && (
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !user.trim()}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-atlas-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-atlas-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <KeyRound size={14} />
          {submitting ? "Minting token…" : "Sign in"}
        </button>

        <p className="text-center text-2xs text-atlas-slate">
          The dev token is signed with{" "}
          <code className="font-mono">datumcraft.dev.jwt-secret</code> and grants{" "}
          <code className="font-mono">ADMIN</code> + <code className="font-mono">SUPER_ADMIN</code>.
          Production uses your IdP instead.
        </p>
      </form>
    </div>
  );
}
